import { Button, Cell, Grid, Icon, PagedTable, TabItem, Tabs, Tooltip } from 'bold-ui'
import {
  AxisDomain,
  ChartRangeSelector,
  ChartRangeSelectorProps,
  DateRange,
  DateRangeStep,
} from 'bold-ui/lib/components/Chart'
import { SemanasDiasFieldModel } from 'components/form/field/SemanasDiasField'
import { TableBox } from 'components/table/TableBox'
import { usePagedTableProps } from 'components/table/usePagedTableProps'
import { isBefore } from 'date-fns'
import { Medicao, PageParams, SexoEnum } from 'graphql/types.generated'
import moment, { Moment } from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { noop } from 'util/functions'
import { formatNumber } from 'util/number'
import { sexoByIdentidadeGenero } from 'util/sexoByIdentidadeGenero'
import { OBSERVACAO_DISABLE_REMOVE_TOOLTIP_TEXT } from 'view/atendimentos/atendimento-individual/atendimento-observacao/model-atendObservacao'
import { useIsCriancaPreTermo } from 'view/atendimentos/components/hooks/useIsCriancaPreTermo'
import { CidadaoMedicoes } from 'view/atendimentos/detail/soap/aside/medicoes/model'
import { HistoricoMedicaoModel } from 'view/atendimentos/types/model-historicoMedicao'

import { PeriodoGestacaoModel } from '../types/PeriodoGestacaoModel'
import { MedicaoAnteriorAntropometriaForm } from './antropometria/MedicaoAnteriorAntropometriaForm'
import { GraficoAlturaView } from './graficos/antropometria/GraficoAlturaView'
import { GraficoImcView } from './graficos/antropometria/GraficoImcView'
import { GraficoPerimetroCefalicoView } from './graficos/antropometria/GraficoPerimetroCefalicoView'
import { GraficoPesoView } from './graficos/antropometria/GraficoPesoView'
import { LABEL_PRE_TERMO, PeriodoPreTermo } from './graficos/antropometria/util-grafico-pre-termo'
import { MedicaoAnteriorModel } from './model-medicoes'
import { MedicaoResult, renderDataMedicao, renderMedicao } from './render-medicoes'

enum ChartTab {
  Peso,
  Altura,
  Imc,
  PerimetroCefalico,
  AlturaUterina,
}

const MEDICOES_ANTROPOMETRIA: any[keyof Medicao] = [
  'valorPeso',
  'valorAltura',
  'valorImc',
  'valorPerimetroCefalico',
  'valorCircunferenciaAbdominal',
  'perimetroPanturrilha',
]

const TOOLTIP_PRE_TERMO = 'O IMC não se aplica para crianças pré-termo'

const IMC_PRE_TERMO: MedicaoResult = {
  medicao: '-',
  tooltip: TOOLTIP_PRE_TERMO,
}

export interface HistoricoMedicoesAntropometriaViewProps {
  prontuarioId: ID
  cidadao: CidadaoMedicoes
  medicoes: ReadonlyArray<HistoricoMedicaoModel>
  gestacoes: ReadonlyArray<PeriodoGestacaoModel>
  isAtendimentoObservacao: boolean
  dataAtendimento?: Instant
  canEditOrAdd?: boolean
  isRetificacao?: boolean
  onAddMedicaoAnterior?: (medicao: MedicaoAnteriorModel) => void
  onRemoveMedicaoAnterior?: (id: ID) => void
  isGestante?: boolean
  idadeGestacionalAtendimentoAtual?: SemanasDiasFieldModel
}

export function HistoricoMedicoesAntropometriaView(props: HistoricoMedicoesAntropometriaViewProps) {
  const {
    prontuarioId,
    medicoes,
    cidadao,
    dataAtendimento,
    gestacoes,
    canEditOrAdd,
    isAtendimentoObservacao,
    isRetificacao = false,
    onAddMedicaoAnterior = noop,
    onRemoveMedicaoAnterior = noop,
    isGestante = false,
    idadeGestacionalAtendimentoAtual,
  } = props

  const sexoBinario = cidadao.sexo === SexoEnum.MASCULINO || cidadao.sexo === SexoEnum.FEMININO

  const { isPreTermo, isPreTermoNaFaixa64Semanas, inicioFaixaPreTermo, fimFaixaPreTermo } = useIsCriancaPreTermo({
    prontuarioId,
    dataNascimento: cidadao.dataNascimento,
    isSexoBinario: sexoBinario,
    idadeGestacional: idadeGestacionalAtendimentoAtual?.semanas,
  })

  const periodoPreTermo: PeriodoPreTermo | undefined = useMemo(
    () =>
      isPreTermo && inicioFaixaPreTermo && fimFaixaPreTermo
        ? { init: moment(inicioFaixaPreTermo), end: moment(fimFaixaPreTermo) }
        : undefined,
    [isPreTermo, inicioFaixaPreTermo, fimFaixaPreTermo]
  )

  const [currentChartTab, setCurrentChartTab] = useState(ChartTab.Peso)

  const { options, defaultOption } = useMemo(() => {
    if (!cidadao?.dataNascimento) return { options: {}, defaultOption: undefined }
    const isImcTab = currentChartTab === ChartTab.Imc

    const shouldShowZeroToTwoYearsRange = !periodoPreTermo || moment().isAfter(periodoPreTermo.end)

    const rangeSelectorPropsPadrao = getRangeSelectorPropsPadrao(cidadao.dataNascimento, shouldShowZeroToTwoYearsRange)

    if (!periodoPreTermo || isImcTab) return rangeSelectorPropsPadrao
    const rangeSelectorPropsPreTermo = getRangeSelectorPropsPreTermo(periodoPreTermo)

    const options = {
      ...rangeSelectorPropsPreTermo.options,
      ...rangeSelectorPropsPadrao.options,
    }
    const defaultOption = rangeSelectorPropsPadrao.defaultOption ?? rangeSelectorPropsPreTermo.defaultOption

    return {
      options: options,
      defaultOption: defaultOption,
    }
  }, [cidadao?.dataNascimento, periodoPreTermo, currentChartTab])

  const [dataRangeSelecionado, setDataRangeSelecionado] = useState<string>(defaultOption)
  const [dataRange, setDataRange] = useState(options[defaultOption])

  const handleDataRangeChange = (rangeSelecionado: Partial<AxisDomain>) => {
    setDataRange(rangeSelecionado)

    const label = Object.keys(options).find((key) => options[key] === rangeSelecionado)

    if (label) {
      setDataRangeSelecionado(label)
    }
  }

  const shouldRenderPreTermo = dataRangeSelecionado === LABEL_PRE_TERMO

  const medicoesExibir = medicoes.filter((med) => MEDICOES_ANTROPOMETRIA.some((e) => med[e]))

  const sexoCidadao = sexoByIdentidadeGenero(cidadao.sexo, cidadao.identidadeGeneroDbEnum)

  useEffect(() => {
    if (!defaultOption || !options) {
      setDataRangeSelecionado(undefined)
      setDataRange(undefined)
      return
    }

    const selecaoAindaExiste = dataRangeSelecionado && options[dataRangeSelecionado]

    if (selecaoAindaExiste) {
      setDataRange(options[dataRangeSelecionado])
    } else {
      setDataRangeSelecionado(defaultOption)
      setDataRange(options[defaultOption])
    }
  }, [defaultOption, options, dataRangeSelecionado])

  const [tableState, setTableState] = useState<{ pageParams?: PageParams }>({
    pageParams: {
      page: 0,
      size: 5,
      sort: !medicoes.find((m) => m.origem === 'medicoesAnteriores') && ['-dataMedicao'],
    },
  })

  const { page: currentPage, size: itensPerPage } = tableState.pageParams
  const totalElements = medicoesExibir?.length ?? 0
  const totalPages = Math.ceil(totalElements / itensPerPage)

  const handleRemove = (row: HistoricoMedicaoModel) => {
    onRemoveMedicaoAnterior(row.id)
  }

  const content = medicoesExibir?.slice(currentPage * itensPerPage, (currentPage + 1) * itensPerPage) ?? []
  const medicoesPage = {
    content: content,
    pageInfo: {
      number: currentPage,
      size: itensPerPage,
      totalPages: totalPages,
      totalElements: medicoesExibir.length,
      first: currentPage === 0,
      last: currentPage === totalPages - 1,
      numberOfElements: content.length,
    },
  }

  const tableProps = usePagedTableProps({
    loading: false,
    onChange: setTableState,
    result: medicoesPage,
  })

  const renderButtons = (row: HistoricoMedicaoModel) => {
    return (
      <Tooltip
        text={
          row.origem === 'medicoesAnteriores'
            ? 'Excluir'
            : row.origem === 'atendimentoAtual'
            ? 'Medições do atendimento atual devem ser alteradas no SOAP.'
            : !isAtendimentoObservacao
            ? 'Medições registradas em outros atendimentos não podem ser excluídas.'
            : OBSERVACAO_DISABLE_REMOVE_TOOLTIP_TEXT
        }
      >
        <Button
          type='button'
          kind='normal'
          skin='ghost'
          disabled={row.origem !== 'medicoesAnteriores'}
          size='small'
          onClick={() => handleRemove(row)}
        >
          <Icon icon='trashOutline' />
        </Button>
      </Tooltip>
    )
  }

  const handleTabImcClick = () => {
    if (shouldRenderPreTermo) {
      return
    }
    setCurrentChartTab(ChartTab.Imc)
  }

  return (
    <Grid>
      <Cell size={12}>
        {canEditOrAdd !== false && (
          <MedicaoAnteriorAntropometriaForm
            dataNascimentoCidadao={cidadao.dataNascimento}
            dataAtendimento={dataAtendimento}
            onSubmit={onAddMedicaoAnterior}
          />
        )}
      </Cell>
      <Cell size={12}>
        <TableBox>
          <PagedTable<HistoricoMedicaoModel>
            {...tableProps}
            columns={[
              {
                header: 'Data da medição',
                name: 'dataMedicao',
                render: renderDataMedicao,
                style: { width: '6rem' },
              },
              {
                header: 'Peso (kg)',
                name: 'valorPeso',
                render: renderMedicao((med) => ({
                  medicao: med?.valorPeso ? formatNumber(med.valorPeso, 2) : '-',
                })),
                style: { width: '5.5rem' },
              },
              {
                header: 'Altura (cm)',
                name: 'valorAltura',
                render: renderMedicao((med) => ({
                  medicao: med?.valorAltura ? formatNumber(med.valorAltura, 2) : '-',
                })),
                style: { width: '4rem' },
              },
              {
                header: 'IMC (kg/m²)',
                name: 'valorImc',
                render: renderMedicao((med) =>
                  getImcMedicao(med, fimFaixaPreTermo, isPreTermoNaFaixa64Semanas, isPreTermo)
                ),
                style: { width: '4rem' },
              },
              {
                header: 'Per. cefálico (cm)',
                name: 'valorPerimetroCefalico',
                render: renderMedicao((med) => ({
                  medicao: med?.valorPerimetroCefalico ? formatNumber(med?.valorPerimetroCefalico, 2) : '-',
                })),
                style: { width: '7rem' },
              },
              {
                header: 'Circ. abdominal (cm)',
                name: 'valorCircunferenciaAbdominal',
                render: renderMedicao((med) => ({
                  medicao: med?.valorCircunferenciaAbdominal ? formatNumber(med?.valorCircunferenciaAbdominal, 2) : '-',
                })),
                style: { width: '8.2rem' },
              },
              {
                header: 'Per. panturrilha (cm)',
                name: 'perimetroPanturrilha',
                render: renderMedicao((med) => ({
                  medicao: med?.perimetroPanturrilha ? formatNumber(med?.perimetroPanturrilha, 2) : '-',
                })),
                style: { width: '8rem' },
              },
              {
                name: 'buttons',
                render: renderButtons,
                style: { textAlign: 'right', width: '3.5rem' },
              },
            ]}
          />
        </TableBox>
      </Cell>
      {!isRetificacao && (
        <>
          <Cell size={12}>
            <Tabs>
              <TabItem active={currentChartTab === ChartTab.Peso} onClick={() => setCurrentChartTab(ChartTab.Peso)}>
                Peso
              </TabItem>
              <TabItem active={currentChartTab === ChartTab.Altura} onClick={() => setCurrentChartTab(ChartTab.Altura)}>
                Altura
              </TabItem>
              <Tooltip text={shouldRenderPreTermo ? 'O gráfico de IMC não se aplica para crianças pré-termo.' : ''}>
                <TabItem
                  active={currentChartTab === ChartTab.Imc}
                  disabled={shouldRenderPreTermo}
                  onClick={() => handleTabImcClick()}
                >
                  IMC
                </TabItem>
              </Tooltip>
              <TabItem
                active={currentChartTab === ChartTab.PerimetroCefalico}
                onClick={() => setCurrentChartTab(ChartTab.PerimetroCefalico)}
              >
                Perímetro cefálico
              </TabItem>
              <ChartRangeSelector
                key={defaultOption}
                label='Faixa etária'
                style={{ marginLeft: 'auto' }}
                options={options}
                defaultOption={defaultOption}
                onChange={handleDataRangeChange}
              />
            </Tabs>
          </Cell>
          <Cell size={12}>
            {currentChartTab === ChartTab.Peso && (
              <GraficoPesoView
                medicoes={medicoesExibir}
                dataNascimento={moment(cidadao.dataNascimento)}
                dataRange={dataRange as DateRange}
                sexo={cidadao.sexo}
                gestacoes={gestacoes}
                isGestante={isGestante}
                periodoPreTermo={periodoPreTermo}
                shouldRenderPreTermo={shouldRenderPreTermo}
              />
            )}
            {currentChartTab === ChartTab.Altura && (
              <GraficoAlturaView
                medicoes={medicoesExibir}
                dataNascimento={moment(cidadao.dataNascimento)}
                dataRange={dataRange as DateRange}
                sexo={sexoCidadao}
                periodoPreTermo={periodoPreTermo}
                shouldRenderPreTermo={shouldRenderPreTermo}
              />
            )}
            {currentChartTab === ChartTab.Imc && !shouldRenderPreTermo && (
              <GraficoImcView
                medicoes={medicoesExibir}
                dataNascimento={moment(cidadao.dataNascimento)}
                dataRange={dataRange as DateRange}
                sexo={sexoCidadao}
                gestacoes={gestacoes}
                isGestante={isGestante}
                periodoPreTermo={periodoPreTermo}
                shouldRenderPreTermo={shouldRenderPreTermo}
              />
            )}
            {currentChartTab === ChartTab.PerimetroCefalico && (
              <GraficoPerimetroCefalicoView
                dataRange={dataRange as DateRange}
                medicoes={medicoesExibir}
                dataNascimento={moment(cidadao.dataNascimento)}
                sexo={sexoCidadao}
                periodoPreTermo={periodoPreTermo}
                shouldRenderPreTermo={shouldRenderPreTermo}
              />
            )}
          </Cell>
        </>
      )}
    </Grid>
  )
}

function getRangeSelectorPropsPreTermo(periodoPreTermo: PeriodoPreTermo): ChartRangeSelectorProps<Moment> {
  return {
    options: {
      [LABEL_PRE_TERMO]: {
        init: periodoPreTermo.init.toDate(),
        end: periodoPreTermo.end.toDate(),
        step: { amount: 2, unit: 'week' },
      },
    },
    defaultOption: LABEL_PRE_TERMO,
  }
}

function getRangeSelectorPropsPadrao(
  dataNascimentoCidadao: string,
  shouldShowZeroToTwoYearsRange = true
): ChartRangeSelectorProps<Moment> {
  const dataNascimento = moment(dataNascimentoCidadao)
  const idadeAtualEmAnos = moment.duration(moment().diff(dataNascimento)).asYears()
  const opcoesDeFaixaEtaria: Record<string, DateRange> = {}

  const adicionarFaixaEtaria = (
    labelFaixaEtaria: string,
    unidadeTempo: 'month' | 'year',
    limiteInicial: number,
    limiteFinal: number,
    configuracaoStep: DateRangeStep
  ) => {
    const dataInicioFaixaEtaria = dataNascimento.clone().add(limiteInicial, unidadeTempo)
    const dataInicioFaixaEtariaAjustada =
      limiteInicial === 0 ? dataInicioFaixaEtaria.toDate() : dataInicioFaixaEtaria.clone().add(1, 'second').toDate()
    const dataFimFaixaEtaria = dataNascimento.clone().add(limiteFinal, unidadeTempo).toDate()

    opcoesDeFaixaEtaria[labelFaixaEtaria] = {
      init: dataInicioFaixaEtariaAjustada,
      end: dataFimFaixaEtaria,
      step: configuracaoStep,
    }
  }

  if (shouldShowZeroToTwoYearsRange)
    adicionarFaixaEtaria('0 a 2 anos', 'month', 0, 24, {
      amount: 2,
      unit: 'month',
    })

  if (idadeAtualEmAnos > 2)
    adicionarFaixaEtaria('2 a 5 anos', 'month', 24, 60, {
      amount: 2,
      unit: 'month',
    })

  if (idadeAtualEmAnos > 5)
    adicionarFaixaEtaria('5 a 10 anos', 'month', 60, 120, {
      amount: 4,
      unit: 'month',
    })

  if (idadeAtualEmAnos > 10)
    adicionarFaixaEtaria('10 a 15 anos', 'month', 120, 180, {
      amount: 4,
      unit: 'month',
    })

  if (idadeAtualEmAnos > 15)
    adicionarFaixaEtaria('15 a 19 anos', 'month', 180, 228, {
      amount: 4,
      unit: 'month',
    })

  if (idadeAtualEmAnos > 19)
    adicionarFaixaEtaria('19 a 30 anos', 'year', 19, 30, {
      amount: 6,
      unit: 'month',
    })

  if (idadeAtualEmAnos > 30) {
    for (let limiteInicial = 30; idadeAtualEmAnos > limiteInicial; limiteInicial += 10) {
      adicionarFaixaEtaria(`${limiteInicial} a ${limiteInicial + 10} anos`, 'year', limiteInicial, limiteInicial + 10, {
        amount: 6,
        unit: 'month',
      })
    }
  }

  const faixaEtariaAtual = Object.keys(opcoesDeFaixaEtaria).slice(-1)[0]

  return { options: opcoesDeFaixaEtaria, defaultOption: faixaEtariaAtual }
}

const getImcMedicao = (
  med: HistoricoMedicaoModel,
  fimFaixaPreTermo: Date | undefined,
  isPreTermoNaFaixa64Semanas: boolean,
  isPreTermo: boolean
): MedicaoResult => {
  const antesDoFimFaixa = isPreTermo && fimFaixaPreTermo && isBefore(new Date(med.dataMedicao), fimFaixaPreTermo)
  const imcNaoAplicavel = isPreTermoNaFaixa64Semanas || antesDoFimFaixa

  if (imcNaoAplicavel) return IMC_PRE_TERMO

  return { medicao: med?.valorImc ? formatNumber(med.valorImc, 2) : '-' }
}
